import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, ArrowRight, Archive, Pencil, X, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';
import type { Organization, VatPeriod } from '../lib/types';

interface CompanyStats {
  org_id: string;
  cash_on_hand: number;
  ar_outstanding: number;
  ap_outstanding: number;
  month_income: number;
  month_expense: number;
}

export default function Companies() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { orgs, setCurrent, refresh, isPlatformAdmin } = useOrg();
  const [stats, setStats] = useState<Record<string, CompanyStats>>({});
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);

  useEffect(() => {
    if (orgs.length === 0) { setStats({}); return; }

    // Fetch a few headline numbers per org.
    (async () => {
      const orgIds = orgs.map((o) => o.id);
      const startOfMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; })();

      const [ar, ap, bal, jLines] = await Promise.all([
        supabase
          .from('dodobooks_invoices')
          .select('org_id, total, amount_paid')
          .in('org_id', orgIds)
          .in('status', ['sent', 'overdue']),
        supabase
          .from('dodobooks_bills')
          .select('org_id, total, amount_paid')
          .in('org_id', orgIds)
          .eq('status', 'approved'),
        supabase
          .from('dodobooks_account_balances')
          .select('org_id, balance, code')
          .in('org_id', orgIds),
        supabase
          .from('dodobooks_journal_lines')
          .select(`
            debit, credit,
            account:dodobooks_accounts!inner(org_id, account_type),
            journal:dodobooks_journals!inner(date, org_id, is_posted)
          `)
          .in('journal.org_id', orgIds)
          .in('account.org_id', orgIds)
          .eq('journal.is_posted', true)
          .gte('journal.date', startOfMonth),
      ]);

      const arBy: Record<string, number> = {};
      for (const r of (ar.data ?? []) as any[]) arBy[r.org_id] = (arBy[r.org_id] ?? 0) + (Number(r.total) - Number(r.amount_paid));
      const apBy: Record<string, number> = {};
      for (const r of (ap.data ?? []) as any[]) apBy[r.org_id] = (apBy[r.org_id] ?? 0) + (Number(r.total) - Number(r.amount_paid));
      const cashBy: Record<string, number> = {};
      for (const r of (bal.data ?? []) as any[]) {
        if (/^1[01]/.test(r.code)) cashBy[r.org_id] = (cashBy[r.org_id] ?? 0) + Number(r.balance ?? 0);
      }
      const incomeBy: Record<string, number> = {};
      const expenseBy: Record<string, number> = {};
      for (const row of (jLines.data ?? []) as any[]) {
        const t = row.account?.account_type;
        const orgId = row.account?.org_id;
        if (!orgId) continue;
        if (t === 'revenue') incomeBy[orgId] = (incomeBy[orgId] ?? 0) + Number(row.credit) - Number(row.debit);
        else if (t === 'expense' || t === 'cogs') expenseBy[orgId] = (expenseBy[orgId] ?? 0) + Number(row.debit) - Number(row.credit);
      }

      const out: Record<string, CompanyStats> = {};
      for (const o of orgs) {
        out[o.id] = {
          org_id: o.id,
          cash_on_hand: cashBy[o.id] ?? 0,
          ar_outstanding: arBy[o.id] ?? 0,
          ap_outstanding: apBy[o.id] ?? 0,
          month_income: incomeBy[o.id] ?? 0,
          month_expense: expenseBy[o.id] ?? 0,
        };
      }
      setStats(out);
    })();
  }, [orgs]);

  function openCompany(orgId: string) {
    setCurrent(orgId);
    navigate('/dashboard');
  }

  return (
    <div className="min-h-screen">
      {/* Slim admin top bar */}
      <header className="border-b border-line bg-ink-2">
        <div className="max-w-6xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="DodoBooks" width={36} height={36} className="rounded-md" />
            <div>
              <div className="font-serif text-lg font-bold leading-none">DodoBooks</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-text-mute mt-1">Admin · CloudDodo</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-text-dim font-mono">
              {user?.email}
              {isPlatformAdmin && <span className="ml-2 pill pill-paid">Platform admin</span>}
            </div>
            <button onClick={() => signOut()} className="btn btn-ghost btn-sm">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="h-page">{isPlatformAdmin ? 'All companies' : 'Your companies'}</h1>
            <p className="text-text-dim text-sm mt-2">
              {orgs.length === 0
                ? (isPlatformAdmin ? 'Add your first business below to get started.' : 'No companies yet. Ask the platform admin to add yours.')
                : `Managing ${orgs.length} ${orgs.length === 1 ? 'business' : 'businesses'}. Click any card to open its books.`}
            </p>
          </div>
          {isPlatformAdmin && (
            <button onClick={() => setAdding(true)} className="btn btn-primary"><Plus size={16} /> Add company</button>
          )}
        </div>

        {orgs.length === 0 ? (
          <div className="card text-center py-16">
            <Building2 size={36} className="mx-auto text-text-mute mb-4" />
            <h2 className="h-section mb-2">No companies yet</h2>
            <p className="text-sm text-text-dim mb-6 max-w-md mx-auto">
              {isPlatformAdmin
                ? 'Add a company to start keeping books for it. Each one gets its own chart of accounts, VAT codes, invoices, bills, ledger and reports — fully isolated from the others.'
                : 'You don\'t have any companies yet. The platform admin needs to create one and add you to it.'}
            </p>
            {isPlatformAdmin && (
              <button onClick={() => setAdding(true)} className="btn btn-primary">
                <Plus size={16} /> Add your first company
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5">
            {orgs.map((o) => {
              const s = stats[o.id];
              return (
                <div key={o.id} className="card card-hover group cursor-pointer relative" onClick={() => openCompany(o.id)}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditing(o); }}
                    className="absolute top-4 right-4 text-text-mute hover:text-text opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit company"
                  >
                    <Pencil size={14} />
                  </button>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-lg bg-emerald/15 text-emerald grid place-items-center font-serif font-bold text-lg shrink-0">
                      {o.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg font-semibold leading-tight">{o.name}</div>
                      <div className="text-[11px] text-text-mute font-mono mt-1 truncate">
                        {o.is_vat_registered ? `VAT ${o.vat_number ?? 'registered'}` : 'Not VAT registered'}
                        {o.reg_number ? ` · ${o.reg_number}` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Stat label="Cash on hand" value={s?.cash_on_hand} accent="emerald" />
                    <Stat label="Owed to you" value={s?.ar_outstanding} accent="gold" />
                    <Stat label="You owe" value={s?.ap_outstanding} accent="rust" />
                    <Stat label="Month net" value={(s?.month_income ?? 0) - (s?.month_expense ?? 0)} accent="emerald" />
                  </div>

                  <div className="flex items-center justify-between border-t border-line pt-3">
                    <span className="text-xs text-text-mute font-mono uppercase tracking-widest">
                      {o.vat_period.replace('_', '-')} · {o.base_currency}
                    </span>
                    <span className="text-sm text-emerald font-medium flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                      Open books <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {adding && <CompanyDialog onClose={() => setAdding(false)} onSaved={async (newOrgId) => {
        await refresh();
        setAdding(false);
        if (newOrgId) { setCurrent(newOrgId); navigate('/dashboard'); }
      }} />}

      {editing && <CompanyDialog org={editing} onClose={() => setEditing(null)} onSaved={async () => { await refresh(); setEditing(null); }} />}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | undefined; accent: 'emerald' | 'gold' | 'rust' }) {
  const accentClass = accent === 'emerald' ? 'text-emerald' : accent === 'gold' ? 'text-gold' : 'text-rust';
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-text-mute mb-0.5">{label}</div>
      <div className={`font-serif font-semibold text-base ${accentClass} num`}>
        {value == null ? '—' : formatMoney(value)}
      </div>
    </div>
  );
}

function CompanyDialog({ org, onClose, onSaved }: { org?: Organization; onClose: () => void; onSaved: (newOrgId?: string) => void }) {
  const { user } = useAuth();
  const isEdit = !!org;
  const [form, setForm] = useState({
    name: org?.name ?? '',
    legal_name: org?.legal_name ?? '',
    reg_number: org?.reg_number ?? '',
    vat_number: org?.vat_number ?? '',
    paye_number: org?.paye_number ?? '',
    is_vat_registered: org?.is_vat_registered ?? false,
    vat_period: (org?.vat_period ?? 'two_monthly') as VatPeriod,
    city: org?.city ?? '',
    phone: org?.phone ?? '',
    email: org?.email ?? '',
    // Owner credentials — only used when creating a NEW org
    owner_email: '',
    owner_password: '',
    owner_full_name: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true); setErr(null);
    if (isEdit) {
      const payload = {
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || form.name.trim(),
        reg_number: form.reg_number.trim() || null,
        vat_number: form.vat_number.trim() || null,
        paye_number: form.paye_number.trim() || null,
        is_vat_registered: form.is_vat_registered,
        vat_period: form.vat_period,
        city: form.city.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('dodobooks_organizations').update(payload).eq('id', org!.id);
      setBusy(false);
      if (error) setErr(error.message);
      else onSaved();
    } else {
      // New company — create the owner user too, atomically, via the edge function.
      const ownerEmail = form.owner_email.trim().toLowerCase();
      if (!ownerEmail || !form.owner_password || form.owner_password.length < 6) {
        setBusy(false);
        setErr('Owner email and a password of at least 6 characters are required.');
        return;
      }
      const { data, error } = await supabase.functions.invoke('dodobooks-create-user', {
        body: {
          email: ownerEmail,
          password: form.owner_password,
          full_name: form.owner_full_name.trim() || null,
          new_org: {
            name: form.name.trim(),
            legal_name: form.legal_name.trim() || form.name.trim(),
            reg_number: form.reg_number.trim() || null,
            vat_number: form.vat_number.trim() || null,
            is_vat_registered: form.is_vat_registered,
            vat_period: form.vat_period,
            city: form.city.trim() || null,
            phone: form.phone.trim() || null,
          },
        },
      });
      setBusy(false);
      if (error) { setErr(error.message); return; }
      if (data?.error) { setErr(data.error); return; }
      onSaved(data?.org_id);
    }
  }

  async function archive() {
    if (!org) return;
    setArchiving(true);
    const { error } = await supabase
      .from('dodobooks_organizations')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', org.id);
    setArchiving(false);
    if (error) setErr(error.message);
    else onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-line sticky top-0 bg-surface z-10">
          <h2 className="h-section">{isEdit ? `Edit ${org!.name}` : 'Add a company'}</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="field col-span-2">
            <label>Trading name *</label>
            <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Vereeniging Coffee Co." />
          </div>
          <div className="field col-span-2">
            <label>Legal name</label>
            <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="e.g. Vereeniging Coffee Co. (Pty) Ltd" />
          </div>
          <div className="field"><label>Reg. number</label><input value={form.reg_number} onChange={(e) => setForm({ ...form, reg_number: e.target.value })} placeholder="2020/123456/07" /></div>
          <div className="field"><label>City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Vereeniging" /></div>
          <div className="field"><label>Phone</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+27 …" /></div>
          <div className="field"><label>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hello@business.co.za" /></div>

          <div className="col-span-2 flex items-center gap-3 bg-ink-2 border border-line rounded-md px-3 py-2">
            <input
              id="vatreg"
              type="checkbox"
              className="accent-emerald w-4 h-4"
              checked={form.is_vat_registered}
              onChange={(e) => setForm({ ...form, is_vat_registered: e.target.checked })}
            />
            <label htmlFor="vatreg" className="text-sm font-sans normal-case tracking-normal text-text">
              VAT-registered with SARS
            </label>
          </div>

          {form.is_vat_registered && (
            <>
              <div className="field"><label>VAT number</label><input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="4xxxxxxxxx" /></div>
              <div className="field"><label>VAT period</label>
                <select value={form.vat_period} onChange={(e) => setForm({ ...form, vat_period: e.target.value as VatPeriod })}>
                  <option value="monthly">Monthly</option>
                  <option value="two_monthly">Two-monthly</option>
                  <option value="six_monthly">Six-monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="field col-span-2"><label>PAYE number (if registered)</label><input value={form.paye_number} onChange={(e) => setForm({ ...form, paye_number: e.target.value })} placeholder="7xxxxxxxxx" /></div>
            </>
          )}

          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

          {!isEdit && (
            <>
              <div className="col-span-2 mt-2 pt-3 border-t border-line">
                <div className="text-[10px] uppercase tracking-widest font-mono text-emerald mb-2">Owner login</div>
                <p className="text-xs text-text-dim mb-3">Create the account the customer will use to sign in. They'll be added as admin of this company only — they won't see your other companies.</p>
              </div>
              <div className="field"><label>Owner email *</label><input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="owner@business.co.za" /></div>
              <div className="field"><label>Temporary password *</label><input required type="text" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} placeholder="6+ characters" /></div>
              <div className="field col-span-2"><label>Owner full name (optional)</label><input value={form.owner_full_name} onChange={(e) => setForm({ ...form, owner_full_name: e.target.value })} placeholder="Jane Smith" /></div>
              <p className="col-span-2 text-xs text-text-mute">
                We'll auto-create this company's chart of accounts (34 SA accounts, codes 1000–6900) and four VAT codes (STD 15%, ZER, EXM, OUT) the moment you click create. Base currency ZAR, March year-start. All editable later in Settings. The owner gets admin role and can invite more users to their team from inside the app.
              </p>
            </>
          )}

          <div className="col-span-2 flex items-center justify-between pt-2">
            <div>
              {isEdit && (
                <button type="button" onClick={archive} disabled={archiving} className="btn btn-ghost text-rust disabled:opacity-50">
                  <Archive size={14} /> {archiving ? 'Archiving…' : 'Archive'}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button type="submit" disabled={busy || !form.name.trim()} className="btn btn-primary disabled:opacity-50">
                {busy ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create company')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
